import React, { useContext, useState } from 'react';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';
import { useParams } from 'react-router-dom';

import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { Box, Button, Divider, Drawer, Typography } from '@mui/material';

import { RootState } from '../../../../app/rootReducer';
import { IconLink } from '../../../../common/components/IconLink';
import { NoraButton } from '../../../../common/components/v2';
import { Resource } from '../../../../common/constants/resource';
import ElementLocators from '../../../../common/ElementLocators';
import { FeatureFlagsContext } from '../../../../common/featureFlagsWrapper';
import { tasksResolved } from '../../../../common/helpers';
import { SnackbarContext } from '../../../layout/SnackbarContext';
import { ITask } from '../../../tasks/interfaces/ITask';
import { getAllTasks } from '../../../tasks/slice/taskListSlice';
import { useDecisionSubmit } from '../../decision/useDecisionSubmit';
import { InvalidDrawer } from '../InvalidDrawer';
import styles from '../styles.module.scss';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export default function AgreeToReinstate() {
  const { id } = useParams<{ id: string }>();
  const [isOpen, setIsOpen] = useState(false);
  const tasks: ITask[] = useSelector(state => getAllTasks(state.taskList));
  const isValid = tasksResolved(tasks);
  const { submitCreate } = useDecisionSubmit(id || '', 'reinstate_agree');

  const { openSnacks, setSnacks } = useContext(SnackbarContext);
  const { flags } = useContext(FeatureFlagsContext);
  const isReinstatementLetterAutomationEnabled = Boolean(
    flags.isReinstatementLetterAutomationEnabled,
  );

  function onClick() {
    if (id) {
      submitCreate(
        {},
        null,
        undefined,
        false,
        isReinstatementLetterAutomationEnabled
          ? { enabled: true, openSnacks, updateSnacks: setSnacks }
          : undefined,
      );
    }
    setIsOpen(false);
  }

  return (
    <>
      {!isValid && (
        <InvalidDrawer
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          labelCopy={Resource.AGREE_TO_REINSTATE}
        />
      )}
      {isValid && (
        <Drawer
          open={isOpen}
          anchor="right"
          variant="persistent"
          classes={{ paper: styles.Drawer }}
          transitionDuration={{ enter: 0, exit: 0 }}
        >
          <Box mt={6}>
            <Box
              m={3}
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body1">
                {Resource.AGREE_TO_REINSTATE}
              </Typography>
              <Button onClick={() => setIsOpen(false)}>
                <Typography variant="subtitle2" color="textSecondary">
                  Cancel
                </Typography>
              </Button>
            </Box>
            <Divider />
          </Box>
          <Box>
            <NoraButton
              name={ElementLocators.LOCATOR_REVIEW_APPROVAL_BUTTON}
              className={styles.DrawerButton}
              onClick={onClick}
            >
              {Resource.CONFIRM_DECISION}
            </NoraButton>
          </Box>
        </Drawer>
      )}
      <IconLink
        onClick={() => setIsOpen(true)}
        icon={faCheck}
        copy={Resource.AGREE_TO_REINSTATE}
        locator={ElementLocators.APPROVE_DECISION_LINK}
      />
    </>
  );
}
